import React, { memo, useState, useEffect, useRef, useCallback } from 'react';
import { Box, Paper, Typography, Card, CardContent, Grid, Chip, Button, IconButton, CircularProgress } from '@mui/material';
import { Car, Fuel, Gauge, Search, Database, BrainCircuit, Calendar, Tag, Palette, ImageOff, Zap, Type, BarChart2, Download, Globe, Target, Users, FileText, Save, Activity, LineChart } from 'lucide-react';
import Carousel from 'react-material-ui-carousel';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { formatPrice } from '../../../utils/utils'; // Adjust path as needed
import SalesReportButton from './SalesReportButton';

// Helper function for radar charts
function createRadarChart(chartData, containerId) {
  const ctx = document.getElementById(containerId).getContext('2d');
  
  const config = {
    type: 'radar',
    data: {
      labels: Object.keys(chartData.data),
      datasets: [{
        label: chartData.title,
        data: Object.values(chartData.data),
        fill: true,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgb(54, 162, 235)',
        pointBackgroundColor: 'rgb(54, 162, 235)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(54, 162, 235)'
      }]
    },
    options: {
      elements: {
        line: {
          borderWidth: 3
        }
      },
      plugins: {
        title: {
          display: true,
          text: chartData.title
        }
      }
    }
  };
  
  return new Chart(ctx, config);
}

// --- Tool Display Mapping ---
const toolDisplayMap = {
  car_stock_search: { label: 'Ricerca Veicoli', icon: <Search size={14} /> },
  car_configurator: { label: 'Catalogo Eurotax', icon: <Car size={14} /> },
  knowledge_search_engine: { label: 'Ricerca Knowledge Base', icon: <Database size={14} /> },
  customer_chat_analytics: { label: 'Analisi Dati CRM', icon: <LineChart size={14} /> },
  dealership_skill_catalog: { label: 'Catalogo Skill', icon: <BrainCircuit size={14} /> },
  dealership_skill_apply: { label: 'Skill Operativa', icon: <Target size={14} /> },
  car_insights_analysis: { label: 'Analisi Vendite', icon: <BarChart2 size={14} /> },
  vehicle_seo_data: { label: 'Dati SEO Veicolo', icon: <Car size={14} /> },
  seo_existing_analysis: { label: 'Analisi SEO Esistente', icon: <Search size={14} /> },
  seo_content_generation: { label: 'Generazione SEO', icon: <FileText size={14} /> },
  seo_content_save: { label: 'Salvataggio SEO', icon: <Save size={14} /> },
  VehicleSEODataTool: { label: 'Dati SEO Veicolo', icon: <Car size={14} /> },
  SEOExistingAnalysisTool: { label: 'Analisi SEO Esistente', icon: <Search size={14} /> },
  SEOContentGenerationTool: { label: 'Generazione SEO', icon: <FileText size={14} /> },
  SEOContentSaveTool: { label: 'Salvataggio SEO', icon: <Save size={14} /> },
  SEOCompetitorAnalysisTool: { label: 'Analisi Concorrenza SEO', icon: <Activity size={14} /> },
  SEOSimilarVehiclesTool: { label: 'Veicoli Simili SEO', icon: <Target size={14} /> },
  SEOAudienceAnalysisTool: { label: 'Analisi Pubblico SEO', icon: <Users size={14} /> },
  // Add other tools as needed
  default: { label: 'Analisi', icon: <BrainCircuit size={14} /> }
};

const VEHICLE_RESULT_TOOLS = new Set(['car_stock_search', 'car_configurator']);

const normalizeToolList = (tool) => {
  if (Array.isArray(tool)) return tool.filter(Boolean);
  if (typeof tool === 'string' && tool.trim()) return [tool.trim()];
  return [];
};

const isVehicleResultTool = (tool) => (
  normalizeToolList(tool).some((toolName) => VEHICLE_RESULT_TOOLS.has(toolName))
);

// --- CarCard Component ---
const CarCard = memo(({ car, darkMode, onShowDetails }) => {
  const [imageError, setImageError] = useState(false);
  const vehicleId = car?.vehicleid || car?.vehicleId || null;
  const isCatalogItem = typeof vehicleId === 'string' && vehicleId.startsWith('catalog_');
  
  // Debug logging to understand what data is available
  useEffect(() => {
    //console.log("Car data in CarCard:", car);
    //console.log("Image URL:", car.urlmainimage); // Fix case sensitivity
  }, [car]);

  // Fixed case sensitivity in property name and added additional fallbacks
  const imageUrl = !imageError && (
    car.urlmainimage || 
    car.urlMainImage || 
    (car.imagesUrl && car.imagesUrl.length > 0 && car.imagesUrl[0])
  );

  // Handle click event
  const handleClick = () => {
    if (onShowDetails) {
      //console.log("Car clicked, passing data:", car);
      onShowDetails(car); // Pass the full car object
    }
  };

  // Handle "Genera seo" button click
  const handleGenerateSeo = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    
    if (!vehicleId || isCatalogItem) {
      console.error("Cannot generate SEO: Missing vehicle ID");
      return;
    }
    
    // Create a message event that the internal assistant will process
    const message = `Generate seo per vehicleid: ${vehicleId}`;
    
    // Send the message to the chat
    window.postMessage({
      type: 'internalAssistantMessage',
      message: message
    }, '*');
  };

  // Fallback for missing data & more complete title
  const brand = car.brand || 'Marca Sconosciuta';
  const model = car.model || 'Modello Sconosciuto';
  // Use versiondescription if available, otherwise version
  const version = car.versiondescription || car.version || ''; 
  const title = `${brand} ${model} ${version}`.trim().replace(/\s+/g, ' '); // Combine and clean up spaces
  const altText = title; // Use combined title for alt text
  
  // Data points for display, handling potential missing values
  const displayPrice = formatPrice(
    car.salePrice || car.saleprice || car.price || car.listPrice || car.list_price
  );
  const displayYear = car.registrationYear || car.registrationyear || car.year || null;
  const kmValue = car.km ?? car.kilometers ?? car.mileage;
  const displayKm = Number.isFinite(Number(kmValue)) ? Number(kmValue).toLocaleString() : null;
  const displayKw = car.kwPower || car.kwpower || car.power_kw || null;
  const displayFuel = car.fuel || car.fuelType || car.fuel_type || null;
  const displayVehicleType = car.usagetype || car.vehicleType || car.vehicletype || car.body_type || null;
  const isNeopatentati = car.isYoungDriverFit === true;
  const specItems = [
    displayYear ? { icon: <Calendar size={14} />, value: String(displayYear) } : null,
    displayKm ? { icon: <Gauge size={14} />, value: `${displayKm} km` } : null,
    displayKw ? { icon: <Zap size={14} />, value: `${displayKw} kW` } : null,
    displayFuel ? { icon: <Fuel size={14} />, value: String(displayFuel) } : null,
    displayVehicleType ? { icon: <Type size={14} />, value: String(displayVehicleType), capitalize: true } : null,
  ].filter(Boolean).slice(0, 4);

  // More permissive data validation - only require vehicleid
  const hasValidData = Boolean(car && vehicleId);

  // If we don't have valid car data, show a placeholder with debug info
  if (!hasValidData) {
    console.warn("Invalid car data received:", car);
    return (
      <Card sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        width: '100%',
        boxShadow: 2,
        borderRadius: 2,
      }}>
        <Box sx={{ 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}>
          <ImageOff size={40} color="#bdbdbd" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
            Dati veicolo incompleti
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      position: 'relative',
      width: '100%',
      boxShadow: 2,
      borderRadius: 2,
      cursor: onShowDetails ? 'pointer' : 'default', // Add cursor pointer if clickable
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      '&:hover': onShowDetails ? {
          transform: 'translateY(-4px)',
          boxShadow: 6,
      } : {},
    }}
    onClick={handleClick} // Add onClick handler
    >
      <Box
        sx={{
          height: 180, // Slightly reduced height
          backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          borderTopLeftRadius: 'inherit',
          borderTopRightRadius: 'inherit',
          position: 'relative', // Added for badge positioning
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={altText}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={(e) => {
              // Log the error for debugging
              console.error("Image failed to load:", imageUrl);
              e.target.onerror = null; // Prevent infinite loop
              e.target.style.display = 'none'; // Hide broken image element
              setImageError(true); // Set state to show placeholder
            }}
            loading="lazy" // Add lazy loading
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: darkMode ? '#424242' : '#f5f5f5' }}>
            <ImageOff size={40} color="#bdbdbd" /> 
          </Box>
        )}
        
        {/* Show hybrid/electric badge if applicable */}
        {car.hybridDescription && (
          <Chip 
            label={car.hybridDescription}
            size="small"
            color="primary"
            sx={{ 
              position: 'absolute', 
              top: 8, 
              left: 8,
              fontSize: '0.7rem',
              height: '22px'
            }}
          />
        )}
        
        {/* Show status if car is booked/reserved */}
        {car.statusDescription && car.statusDescription !== "Published" && (
          <Chip 
            label={car.statusDescription}
            size="small"
            color="secondary"
            sx={{ 
              position: 'absolute', 
              top: 8, 
              right: 8,
              fontSize: '0.7rem',
              height: '22px'
            }}
          />
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1, p: 1.5 }}> {/* Adjusted padding */}
        {/* Top section: Title, Neopatentati Tag, Price */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}> {/* Allow title box to grow and shrink */}
            <Typography 
              variant="h6" 
              component="div" 
              sx={{ 
                fontWeight: 'bold',
                fontSize: '0.95rem', // Slightly smaller for potentially longer titles
                lineHeight: 1.3, 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                // Allow two lines for longer names
                display: '-webkit-box',
                WebkitLineClamp: '2',
                WebkitBoxOrient: 'vertical',
                mb: 0.5 // Add some space below title
              }}
              title={title}
            >
              {title}
            </Typography>
            {/* Neopatentati Chip */} 
            {isNeopatentati && (
              <Chip 
                label="Neopatentati" 
                size="small" 
                color="success" 
                variant="outlined" 
                sx={{ 
                  height: '20px', 
                  fontSize: '0.7rem', 
                  mr: 1, // Add some spacing if other elements follow
                  borderColor: 'success.light',
                  color: 'success.dark',
                  bgcolor: 'success.lighter'
                }}
              />
            )}
          </Box>
          <Typography 
            variant="h6" 
            color="primary" 
            sx={{ 
              fontWeight: 'bold',
              fontSize: '1.0rem', // Slightly reduced size
              whiteSpace: 'nowrap', // Prevent price wrapping
              ml: 1, // Add margin
              alignSelf: 'center' // Vertically center price
            }}
          >
            {displayPrice}
          </Typography>
        </Box>

        {specItems.length > 0 && (
          <Grid container spacing={1} sx={{ fontSize: '0.8rem', mt: 1 }}>
            {specItems.map((spec, idx) => (
              <Grid item xs={6} sm={3} key={idx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                  {spec.icon}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: 'inherit',
                      textTransform: spec.capitalize ? 'capitalize' : 'none'
                    }}
                  >
                    {spec.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
        
        {!isCatalogItem && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileText size={14} />}
              onClick={handleGenerateSeo}
              sx={{ 
                textTransform: 'none',
                fontSize: '0.75rem',
                borderRadius: 1.5,
                fontWeight: 500,
                py: 0.5,
                px: 1,
                bgcolor: 'transparent',
                color: darkMode ? '#fff' : 'primary.main',
                borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'primary.main',
                '&:hover': {
                  bgcolor: 'rgba(67, 97, 238, 0.1)',
                  borderColor: 'primary.main',
                }
              }}
            >
              Genera SEO
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
});

// --- Typing Animation Keyframes (Keep outside components) ---
const typingAnimationKeyframes = `
@keyframes typingDots {
  0%, 20% { content: "."; }
  40% { content: ".."; }
  60%, 100% { content: "..."; }
}
@keyframes fadeIn { 
  from { opacity: 0; transform: translateY(10px); } 
  to { opacity: 1; transform: translateY(0); } 
}
@keyframes pulse { 
  0% { opacity: 0.6; } 
  50% { opacity: 1; } 
  100% { opacity: 0.6; } 
}
@keyframes caretBlink {
  0%, 45% { opacity: 0; }
  50%, 100% { opacity: 1; }
}
`;

const AssistantAvatar = memo(({ darkMode }) => (
  <Box
    sx={{
      width: 34,
      height: 34,
      borderRadius: '50%',
      background: darkMode
        ? 'linear-gradient(135deg, rgba(14,165,233,0.9), rgba(34,197,94,0.85))'
        : 'linear-gradient(135deg, rgba(3,105,161,0.9), rgba(22,163,74,0.85))',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontWeight: 600,
      letterSpacing: '0.5px',
      boxShadow: darkMode
        ? '0 4px 12px rgba(12,20,48,0.35)'
        : '0 6px 16px rgba(59,130,246,0.35)',
      flexShrink: 0
    }}
  >
    AI
  </Box>
));

// --- UserMessage Component ---
export const UserMessage = memo(({ content, timestamp, darkMode, message }) => (
  <Box sx={{
    display: 'flex',
    justifyContent: 'flex-end',
    mb: 2,
    animation: 'fadeIn 0.3s ease-out',
  }}>
    <Paper sx={{
      p: 1.5, // Slightly reduced padding
      borderRadius: '18px 18px 4px 18px',
      bgcolor: darkMode ? 'primary.dark' : 'primary.light',
      color: darkMode ? 'white' : 'black',
      maxWidth: '80%',
      boxShadow: 1, // Reduced shadow
      position: 'relative',
      // Consider removing pseudo-element for performance if needed
      // '&::after': { ... }
    }}>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {content}
      </Typography>
      
      {/* Display file attachment if present */}
      {message?.attachment && (
        <FileAttachment attachment={message.attachment} darkMode={darkMode} />
      )}
      
      {(message?.sender_message_time || timestamp) && (
        <Typography variant="caption" sx={{ 
          display: 'block', 
          mt: 0.5, 
          textAlign: 'right',
          opacity: 0.7,
          fontSize: '0.7rem'
        }}>
          {message?.sender_message_time 
            ? new Date(message.sender_message_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          }
        </Typography>
      )}
    </Paper>
  </Box>
));

// --- AssistantMessage Component ---
export const AssistantMessage = memo(({ content, isLoading, tool = [], darkMode, message }) => {
  const toolArray = Array.isArray(tool) ? tool : (tool ? [tool] : []);
  const toolPriority = {
    dealership_skill_catalog: 0,
    dealership_skill_apply: 0,
    knowledge_search_engine: 1,
    customer_chat_analytics: 2,
    car_insights_analysis: 2,
    car_configurator: 3,
    car_stock_search: 4,
    vehicle_seo_data: 5,
    seo_existing_analysis: 5,
    seo_content_generation: 5,
    seo_content_save: 5,
  };
  const orderedTools = [...new Set(toolArray)].sort(
    (a, b) => (toolPriority[a] ?? 99) - (toolPriority[b] ?? 99)
  );
  const visibleTools = orderedTools.slice(0, 2);
  const hiddenToolsCount = Math.max(orderedTools.length - visibleTools.length, 0);
  const timestamp = message?.bot_message_time || message?.created_at;
  const isStreaming = Boolean(isLoading || message?.loading);
  
  return (
  <Box sx={{
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 1.5,
    mb: 2.5,
    animation: 'fadeIn 0.3s ease-out',
  }}>
    <AssistantAvatar darkMode={darkMode} />
    <Paper sx={{
      p: 2,
      pt: 1.75,
      borderRadius: '18px 18px 18px 6px',
      bgcolor: darkMode ? 'rgba(16,24,40,0.7)' : '#F7F9FC',
      color: darkMode ? 'rgba(255,255,255,0.92)' : 'rgba(15,23,42,0.95)',
      maxWidth: '82%',
      position: 'relative',
      boxShadow: darkMode
        ? '0 12px 35px rgba(15,23,42,0.45)'
        : '0 18px 45px rgba(15,23,42,0.12)',
      border: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.05)',
      backdropFilter: darkMode ? 'blur(6px)' : 'none',
      transition: 'transform 0.2s ease-out',
      '&:hover': {
        transform: 'translateY(-1px)',
      }
    }}>
      {/* Only show tool chips if toolArray has items and is not empty */}
      {orderedTools && orderedTools.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          gap: 0.8, 
          flexWrap: 'wrap', 
          mb: 1.5 // Increased margin bottom slightly
        }}>
          {visibleTools.map((toolName, index) => {
            const displayInfo = toolDisplayMap[toolName] || toolDisplayMap.default;
            return (
              <Chip 
                key={index} 
                icon={displayInfo.icon}
                label={displayInfo.label}
                size="small"
                variant="outlined" // Use outlined variant for less visual weight
                sx={{
                  fontSize: '0.75rem', // Slightly larger font
                  borderRadius: '6px', // Softer corners
                  bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                  color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)',
                  height: '26px', // Explicit height
                  '& .MuiChip-icon': {
                    color: 'inherit', // Ensure icon inherits color
                    marginLeft: '6px', // Adjust icon spacing
                    marginRight: '-4px'
                  },
                  '& .MuiChip-label': {
                     paddingLeft: '6px', // Adjust label spacing
                     paddingRight: '8px'
                  }
                }}
              />
            );
          })}
          {hiddenToolsCount > 0 && (
            <Chip
              label={`+${hiddenToolsCount}`}
              size="small"
              variant="outlined"
              sx={{
                fontSize: '0.75rem',
                borderRadius: '6px',
                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.7)',
                height: '26px',
              }}
            />
          )}
        </Box>
      )}
      
      <Box sx={{
        fontSize: '1rem',
        lineHeight: 1.65,
        wordBreak: 'break-word',
        color: 'inherit'
      }}>
        {isStreaming && !content ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ animation: 'pulse 1.5s infinite', fontSize: '0.95rem', opacity: 0.9 }}>
              Sto pensando
            </Typography>
            <Box component="span" sx={{
              width: 10,
              height: 16,
              borderRadius: '2px',
              backgroundColor: darkMode ? 'rgba(148,163,184,0.8)' : 'rgba(3,105,161,0.8)',
              animation: 'caretBlink 1s steps(1) infinite'
            }} />
          </Box>
        ) : (
          <ReactMarkdown
            components={{}}
            rehypePlugins={[rehypeRaw]}
          >
            {content || ''}
          </ReactMarkdown>
        )}
        {isStreaming && content && (
          <Box component="span"
            sx={{
              display: 'inline-block',
              width: 10,
              height: 16,
              borderRadius: '2px',
              ml: 0.5,
              backgroundColor: darkMode ? 'rgba(148,163,184,0.8)' : 'rgba(3,105,161,0.8)',
              animation: 'caretBlink 1s steps(1) infinite'
            }}
          />
        )}
      </Box>
      
      {/* Display file attachment if present */}
      {message?.attachment && (
        <FileAttachment attachment={message.attachment} darkMode={darkMode} />
      )}
      
      {/* Display analytics chart if present in sources */}
      {message?.sources?.chart_config && (
        <AnalyticsChart chartConfig={message.sources.chart_config} darkMode={darkMode} />
      )}

      {timestamp && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1.5,
            textAlign: 'right',
            fontSize: '0.72rem',
            letterSpacing: '0.02em',
            opacity: darkMode ? 0.55 : 0.65
          }}
        >
          {new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      )}
    </Paper>
  </Box>
  );
});

// --- CarInsightsChart Component ---
// Generic Analytics Chart Component
const AnalyticsChart = memo(({ chartConfig, darkMode }) => {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);
  const [chartJsLoaded, setChartJsLoaded] = useState(false);
  
  // Load Chart.js
  useEffect(() => {
    if (window.Chart) {
      setChartJsLoaded(true);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js';
    script.async = true;
    script.onload = () => setChartJsLoaded(true);
    document.head.appendChild(script);
    
    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);
  
  // Render chart
  useEffect(() => {
    if (!chartJsLoaded || !chartConfig || !canvasRef.current) return;
    
    // Destroy previous chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }
    
    try {
      const ctx = canvasRef.current.getContext('2d');
      
      // Apply dark mode colors to chart config
      const config = JSON.parse(JSON.stringify(chartConfig)); // Deep clone
      
      if (darkMode) {
        // Update text colors for dark mode
        if (config.options?.plugins?.title) {
          config.options.plugins.title.color = 'rgba(255,255,255,0.9)';
        }
        if (config.options?.plugins?.legend?.labels) {
          config.options.plugins.legend.labels.color = 'rgba(255,255,255,0.8)';
        }
        if (config.options?.scales) {
          Object.keys(config.options.scales).forEach(axis => {
            config.options.scales[axis].ticks = {
              ...config.options.scales[axis].ticks,
              color: 'rgba(255,255,255,0.7)'
            };
            config.options.scales[axis].grid = {
              ...config.options.scales[axis].grid,
              color: 'rgba(255,255,255,0.1)'
            };
          });
        }
      }
      
      chartInstance.current = new window.Chart(ctx, config);
    } catch (error) {
      console.error('Error creating analytics chart:', error);
    }
    
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [chartJsLoaded, chartConfig, darkMode]);
  
  if (!chartConfig) return null;
  
  return (
    <Box
      sx={{
        width: '100%',
        height: 400,
        p: 2,
        bgcolor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        borderRadius: 2,
        mt: 2
      }}
    >
      {!chartJsLoaded && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress size={32} />
        </Box>
      )}
      <canvas ref={canvasRef} style={{ maxHeight: '100%' }} />
    </Box>
  );
});

const CarInsightsChart = memo(({ insightsData, darkMode }) => {
  //console.log("CarInsightsChart received data:", insightsData);
  const [activeTab, setActiveTab] = useState(null);
  const [chartJsLoaded, setChartJsLoaded] = useState(false);
  const chartInstances = useRef({});
  const chartCanvasRefs = useRef({});
  
  // Get available analysis types - defined early
  const availableTypes = insightsData?.chart_type ? 
    Object.keys(insightsData.chart_type).filter(type => insightsData.chart_type[type]) : [];
  
  // Generate unique chart ID to avoid conflicts
  const getChartId = useCallback((name) => {
    return `chart-${name}-${Math.random().toString(36).substring(2, 10)}`;
  }, []);
  
  // Clear all charts before rendering new ones
  const clearCharts = useCallback(() => {
    Object.values(chartInstances.current).forEach(chart => {
      if (chart) chart.destroy();
    });
    chartInstances.current = {};
  }, []);
  
  // Create a basic chart (bar chart)
  const createChart = useCallback((data, type = 'bar', name) => {
    if (!data || !window.Chart || !chartCanvasRefs.current[name]) {
      console.error(`Cannot create chart ${name}. Missing dependencies.`, { 
        hasData: !!data, 
        hasChartJS: !!window.Chart, 
        hasCanvas: !!chartCanvasRefs.current[name] 
      });
      return;
    }
    
    try {
      const ctx = chartCanvasRefs.current[name].getContext('2d');
      
      // Destroy existing chart if it exists
      if (chartInstances.current[name]) {
        chartInstances.current[name].destroy();
      }
      
      // Debug the data format
      //console.log(`Creating chart ${name} with data:`, data);
      
      // Ensure we have labels and data in the correct format
      // The API might return data in several different formats
      const labels = data.labels || (Array.isArray(data.data) ? 
        Object.keys(data.data) : 
        (data.data ? Object.keys(data.data) : []));
        
      const values = data.data || (Array.isArray(data.data) ? 
        data.data : 
        (data.data ? Object.values(data.data) : []));
      
      // Generate colors
      const backgroundColor = labels.map((_, i) => {
        const hue = (i * 137.5) % 360; // Golden angle for better color distribution
        return darkMode 
          ? `hsla(${hue}, 80%, 60%, 0.7)`
          : `hsla(${hue}, 70%, 50%, 0.7)`;
      });
      
      // Create chart with proper configuration
      chartInstances.current[name] = new window.Chart(ctx, {
        type: type,
        data: {
          labels: labels,
          datasets: [{
            label: data.title || name,
            data: values,
            backgroundColor: backgroundColor,
            borderColor: backgroundColor.map(color => color.replace('0.7', '1')),
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
              }
            },
            title: {
              display: !!data.title,
              text: data.title || '',
              color: darkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'
            }
          }
        }
      });
      
      //console.log(`Chart ${name} created successfully`);
    } catch (error) {
      console.error(`Error creating chart ${name}:`, error);
    }
  }, [darkMode]);
  
  // Create line chart
  const createLineChart = useCallback((data, name, options = {}) => {
    if (!data || !window.Chart || !chartCanvasRefs.current[name]) {
      console.error(`Cannot create line chart ${name}. Missing dependencies.`);
      return;
    }
    
    //console.log(`Creating line chart: ${name}`, data);
    
    try {
      const ctx = chartCanvasRefs.current[name].getContext('2d');
      
      // Destroy existing chart if it exists
      if (chartInstances.current[name]) {
        chartInstances.current[name].destroy();
      }
      
      // Prepare data
      const labels = data.labels || Object.keys(data.data || {});
      const values = data.values || Object.values(data.data || {});
      
      // Create chart
      chartInstances.current[name] = new window.Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: data.title || name,
            data: values,
            borderColor: darkMode ? 'rgba(132, 99, 255, 1)' : 'rgba(75, 192, 192, 1)',
            backgroundColor: darkMode ? 'rgba(132, 99, 255, 0.2)' : 'rgba(75, 192, 192, 0.2)',
            tension: 0.3,
            fill: true
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false,
              labels: {
                color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              title: {
                display: !!options.yAxisTitle,
                text: options.yAxisTitle,
                color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
              },
              grid: {
                color: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
              },
              ticks: {
                color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
              }
            },
            x: {
              title: {
                display: !!options.xAxisTitle,
                text: options.xAxisTitle,
                color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
              },
              grid: {
                display: false
              },
              ticks: {
                color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
              }
            }
          }
        }
      });
    } catch (error) {
      console.error(`Error creating line chart ${name}:`, error);
    }
  }, [darkMode]);
  
  // Create doughnut chart
  const createDoughnutChart = useCallback((data, name) => {
    if (!data || !window.Chart || !chartCanvasRefs.current[name]) {
      console.error(`Cannot create doughnut chart ${name}. Missing dependencies.`);
      return;
    }
    
    //console.log(`Creating doughnut chart: ${name}`, data);
    
    try {
      const ctx = chartCanvasRefs.current[name].getContext('2d');
      
      // Destroy existing chart if it exists
      if (chartInstances.current[name]) {
        chartInstances.current[name].destroy();
      }
      
      // Prepare data
      const labels = data.labels || Object.keys(data.data || {});
      const values = data.values || Object.values(data.data || {});
      
      // Generate colors
      const backgroundColor = labels.map((_, i) => {
        const hue = (i * 137.5) % 360; // Golden angle approximation for nice spread
        return darkMode 
          ? `hsla(${hue}, 80%, 60%, 0.8)`
          : `hsla(${hue}, 70%, 50%, 0.8)`;
      });
      
      // Create chart
      chartInstances.current[name] = new window.Chart(ctx, {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor,
            borderColor: darkMode ? 'rgba(30,30,30,0.8)' : 'white',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right',
              labels: {
                color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)'
              }
            }
          }
        }
      });
    } catch (error) {
      console.error(`Error creating doughnut chart ${name}:`, error);
    }
  }, [darkMode]);
  
  // Generate layout based on the active tab - moved before renderCharts
  const getChartLayout = useCallback(() => {
    if (!insightsData || !insightsData.chart_data) {
      return [];
    }
    
    const currentType = activeTab || 
      (insightsData.analysis_metadata?.analysis_type || 'market_overview');
    
    //console.log(`Generating chart layout for ${currentType}`);
    
    // Build layout dynamically based on available data
    const chartData = insightsData.chart_data;
    const dynamicLayout = [];
    
    if (currentType === 'transaction_analysis') {
      // Always include these charts for transaction_analysis if data exists
      if (chartData.distribution) {
        dynamicLayout.push({ id: 'distributionChart', title: 'Distribution', cols: 6 });
      }
      
      if (chartData.overall_body_styles) {
        dynamicLayout.push({ id: 'bodyStylesChart', title: 'Body Styles', cols: 6 });
      }
      
      if (chartData.sales_status) {
        dynamicLayout.push({ id: 'salesStatusChart', title: 'Sales Status', cols: 6 });
      }
      
      if (chartData.sales_trend) {
        dynamicLayout.push({ id: 'salesTrendChart', title: 'Sales Trend', cols: 12 });
      }
      
      const salesByKey = Object.keys(chartData).find(key => key.startsWith('sales_by_'));
      if (salesByKey) {
        dynamicLayout.push({ id: 'salesBySegmentChart', title: 'Sales by Segment', cols: 6 });
      }
    } else {
      // For other types, create layout dynamically based on available data
      Object.keys(chartData).forEach(key => {
        if (chartData[key] && (chartData[key].labels || chartData[key].data)) {
          dynamicLayout.push({ 
            id: `${key}Chart`, 
            title: chartData[key].title || key.replace(/_/g, ' '),
            cols: key.includes('trend') || key.includes('overview') ? 12 : 6
          });
        }
      });
    }
    
    //console.log("Generated chart layout:", dynamicLayout);
    return dynamicLayout;
  }, [insightsData, activeTab]);
  
  // Function to render charts based on analysis type
  const renderCharts = useCallback(() => {
    if (!insightsData || !insightsData.chart_data) {
      console.error("No chart data available for rendering");
      return;
    }
    
    // Clear existing charts
    clearCharts();
    
    // Get analysis type from active tab or metadata
    const analysisType = activeTab || 
      (insightsData.analysis_metadata?.analysis_type || 'market_overview');
    
    //console.log(`Rendering charts for analysis type: ${analysisType}`, insightsData.chart_data);
    
    // Simplify and make the chart creation more robust
    const chartData = insightsData.chart_data;
    
    // Add better debug to match the format in the API response
    //console.log("Available chart_data keys:", Object.keys(chartData));
    
    // Handle transaction_analysis type
    if (analysisType === 'transaction_analysis') {
      // Create charts for transaction_analysis
      
      // Distribution chart (common in most views)
      if (chartData.distribution) {
        //console.log("Creating distribution chart");
        createChart(chartData.distribution, 'bar', 'distributionChart');
      }
      
      // Overall body styles
      if (chartData.overall_body_styles) {
        //console.log("Creating body styles chart");
        createChart(chartData.overall_body_styles, 'pie', 'bodyStylesChart');
      }
      
      // Sales status (pie chart)
      if (chartData.sales_status) {
        //console.log("Creating sales status chart");
        createDoughnutChart(chartData.sales_status, 'salesStatusChart');
      }
      
      // Sales trend (line chart)
      if (chartData.sales_trend) {
        //console.log("Creating sales trend chart");
        createLineChart(chartData.sales_trend, 'salesTrendChart', {
          xAxisTitle: 'Period',
          yAxisTitle: 'Number of Sales'
        });
      }
      
      // Any chart key that starts with sales_by
      const salesByKey = Object.keys(chartData).find(key => key.startsWith('sales_by_'));
      if (salesByKey) {
        //console.log(`Creating chart for ${salesByKey}`);
        createChart(chartData[salesByKey], 'bar', 'salesBySegmentChart');
      }
    } else {
      // For other analysis types, just try to create charts for all available data
      Object.entries(chartData).forEach(([key, data]) => {
        if (data && (data.labels || data.data)) {
          const chartId = `${key}Chart`;
          let chartType = 'bar'; // Default type
          
          // Choose chart type based on data characteristics
          if (key.includes('distribution') || key.includes('status')) {
            chartType = 'pie';
          } else if (key.includes('trend') || key.includes('over_time')) {
            chartType = 'line';
          }
          
          //console.log(`Creating chart for ${key} as ${chartType}`);
          createChart(data, chartType, chartId);
        }
      });
    }
  }, [insightsData, activeTab, clearCharts, createChart, createLineChart, createDoughnutChart]);
  
  // Early return check after all functions are defined
  if (!insightsData || !insightsData.chart_data) {
    //console.log("No chart data available, not rendering CarInsightsChart");
    return null;
  }
  
  // If no active tab is set, set it to the first available type
  useEffect(() => {
    if (availableTypes.length > 0 && !activeTab) {
      //console.log("Setting active tab to:", availableTypes[0]);
      setActiveTab(availableTypes[0]);
    }
  }, [availableTypes, activeTab]);
  
  // Initialize charts when component mounts or data changes
  useEffect(() => {
    // Function to load Chart.js
    const loadChartJs = () => {
      if (!window.Chart) {
        //console.log("Loading Chart.js...");
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js';
        script.async = true;
        script.onload = () => {
          //console.log("Chart.js loaded successfully");
          setChartJsLoaded(true);
        };
        script.onerror = (error) => {
          console.error("Failed to load Chart.js:", error);
        };
        document.body.appendChild(script);
        
        return () => {
          document.body.removeChild(script);
        };
      } else {
        //console.log("Chart.js already loaded");
        setChartJsLoaded(true);
      }
    };
    
    loadChartJs();
  }, []);
  
  // Render charts when Chart.js is loaded and canvas refs are available
  useEffect(() => {
    if (chartJsLoaded && insightsData) {
      //console.log("Chart.js loaded and data available, preparing to render charts");
      
      // Debug canvas elements - important check!
      const timer = setTimeout(() => {
        getChartLayout().forEach(chart => {
          const canvas = document.getElementById(chart.id);
          if (!canvas) {
            console.error(`Canvas element with ID ${chart.id} not found!`);
          } else {
            //console.log(`Canvas found: ${chart.id}`);
          }
        });
        renderCharts();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [chartJsLoaded, insightsData, activeTab]);
  
  // Clean up charts when component unmounts
  useEffect(() => {
    return () => {
      Object.values(chartInstances.current).forEach(chart => {
        if (chart) chart.destroy();
      });
    };
  }, []);
  
  return (
    <Box sx={{ mt: 3, mb: 4, mx: 'auto', maxWidth: '98%', overflow: 'hidden' }}>
      <Box sx={{ 
        bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(240,240,240,0.5)', 
        borderRadius: 2,
        p: 2,
        boxShadow: 1
      }}>
        {/* Title and metadata */}
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'medium', color: darkMode ? 'white' : 'black' }}>
          {insightsData.descriptive_information}
        </Typography>
        
        {insightsData.analysis_metadata && (
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>
            Analysis based on {insightsData.analysis_metadata.total_cars_analyzed || 'multiple'} vehicles
            {insightsData.analysis_metadata.date_range && ` over ${insightsData.analysis_metadata.date_range}`}
          </Typography>
        )}
        
        {/* Analysis type tabs - only show if multiple types are available */}
        {availableTypes.length > 1 && (
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 1, 
            mb: 2,
            borderBottom: 1,
            borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'
          }}>
            {availableTypes.map(type => (
              <Button 
                key={type}
                variant={activeTab === type ? 'contained' : 'text'}
                size="small"
                onClick={() => setActiveTab(type)}
                sx={{ 
                  textTransform: 'capitalize',
                  px: 2,
                  borderRadius: '4px 4px 0 0',
                  minWidth: 'auto',
                  bgcolor: activeTab === type ? 
                    (darkMode ? 'primary.dark' : 'primary.light') : 
                    'transparent',
                  color: activeTab === type ?
                    (darkMode ? 'white' : 'black') :
                    (darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'),
                  '&:hover': {
                    bgcolor: activeTab === type ? 
                      (darkMode ? 'primary.dark' : 'primary.light') : 
                      (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)')
                  }
                }}
              >
                {type.replace(/_/g, ' ')}
              </Button>
            ))}
          </Box>
        )}
        
        {/* Charts grid */}
        <Grid container spacing={2}>
          {getChartLayout().map(chart => (
            <Grid item xs={12} md={chart.cols} key={chart.id}>
              <Box sx={{ 
                bgcolor: darkMode ? 'rgba(30,30,30,0.9)' : 'white',
                borderRadius: 1,
                p: 2,
                height: 300,
                boxShadow: 1,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: darkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.8)' }}>
                  {chart.title}
                </Typography>
                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                  <canvas 
                    id={chart.id} 
                    ref={ref => chartCanvasRefs.current[chart.id] = ref}
                    height="240"
                  ></canvas>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
});

// --- MessageList Component ---
export const MessageList = memo(({ messages, darkMode, cleanMessageText, onShowCarDetail }) => {
  // Inject typing animation styles once
  React.useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = typingAnimationKeyframes;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  const lastMessageRef = useRef(null);
  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages]);

  return messages.map((msg, index) => {
    // Determine Carousel Title
    let carouselTitle = "Veicoli trovati:";
    if (msg.sender === 'server' && 
        msg.carouselItems && 
        Array.isArray(msg.carouselItems) && 
        msg.carouselItems.length > 0 && 
        isVehicleResultTool(msg.tool)) {
      
      const firstItem = msg.carouselItems[0];
      const allSameModel = msg.carouselItems.every(
        item => item.brand === firstItem.brand && item.model === firstItem.model
      );

      if (normalizeToolList(msg.tool).includes('car_configurator')) {
        carouselTitle = `${msg.carouselItems.length} allestimenti catalogo trovati:`;
      } else if (allSameModel && firstItem.brand && firstItem.model) {
        carouselTitle = `Risultati per ${firstItem.brand} ${firstItem.model}:`;
      } else {
        carouselTitle = `${msg.carouselItems.length} veicoli trovati:`;
      }
    }

    return (
      <Box
        key={msg.id || `message-${index}`}
        id={`message-${index}`}
        ref={index === messages.length - 1 ? lastMessageRef : null}
        sx={{ width: '100%' }}
      >
        {msg.sender === 'user' ? (
          <UserMessage 
            content={cleanMessageText(msg.text)} 
            timestamp={msg.timestamp}
            darkMode={darkMode}
            message={msg}
          />
        ) : (
          <AssistantMessage 
            content={msg.text} 
            isLoading={msg.loading} 
            tool={msg.tool}
            darkMode={darkMode}
            message={msg}
          />
        )}
        
        {/* Condition to render carousel */}
        {msg.sender === 'server' && 
         msg.carouselItems && 
         Array.isArray(msg.carouselItems) && 
         msg.carouselItems.length > 0 && 
         isVehicleResultTool(msg.tool) && (
          <Box sx={{ mb: 3, mt: 1.5, mx: 'auto', maxWidth: '98%' }}>
            {/* Use the dynamic title */}
            <Typography variant="subtitle2" sx={{ pl: {xs: 0, sm: 1}, mb: 1.5, fontWeight: 'medium' }}>
              {carouselTitle}
            </Typography>
            <Box sx={{ maxWidth: '100%', width: '100%', pl: { xs: 0, sm: 0 } }}> 
              <Carousel 
                autoPlay={false}
                animation="slide"
                navButtonsAlwaysVisible={false} // Hide nav buttons by default, show on hover if needed
                indicators={msg.carouselItems.length > 2} // Show indicators only if more than one slide
                cycleNavigation={false} // Don't cycle
                navButtonsProps={{ style: { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '50%' } }}
                indicatorIconButtonProps={{ style: { padding: '5px', color: 'grey' } }}
                activeIndicatorIconButtonProps={{ style: { color: 'black' } }}
              >
                {Array(Math.ceil(msg.carouselItems.length / 2)).fill().map((_, slideIndex) => (
                  <Grid container spacing={2} key={slideIndex} sx={{ p: 1 }}> 
                    {msg.carouselItems.slice(slideIndex * 2, slideIndex * 2 + 2).map((item, idx) => {
                      // Basic check if item looks like a car object before rendering CarCard
                      if (typeof item === 'object' && item !== null && item.brand) { 
                        return (
                          <Grid item xs={12} sm={6} key={`${slideIndex}-${item.id || idx}`} >
                            <CarCard car={item} darkMode={darkMode} onShowDetails={onShowCarDetail} />
                          </Grid>
                        );
                      } else {
                        // Optionally log or render a placeholder for invalid items
                        console.warn("Invalid item found in carouselItems:", item);
                        return null; 
                      }
                    })}
                  </Grid>
                ))}
              </Carousel>
            </Box>
          </Box>
        )}
        
        {/* Condition to render car insights charts */}
        {msg.sender === 'server' && 
         msg.carInsightsData &&
         (Array.isArray(msg.tool) ? 
           msg.tool.includes("car_insights_analysis") : 
           msg.tool === "car_insights_analysis") && (
          <>
            <SalesReportButton 
              messages={[msg]} 
              darkMode={darkMode} 
            />
            <CarInsightsChart 
              insightsData={msg.carInsightsData} 
              darkMode={darkMode}
            />
          </>
        )}
      </Box>
    );
  });
});

// Add this component to display file attachments
const FileAttachment = ({ attachment, darkMode }) => {
  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📄'; // PDF
      case 'doc':
      case 'docx':
        return '📝'; // Word
      case 'xls':
      case 'xlsx':
        return '📊'; // Excel
      case 'ppt':
      case 'pptx':
        return '📑'; // PowerPoint
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return '🖼️'; // Image
      case 'mp4':
      case 'avi':
      case 'mov':
        return '🎥'; // Video
      case 'mp3':
      case 'wav':
        return '🎵'; // Audio
      default:
        return '📎'; // Generic file
    }
  };

  const isImage = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif'].includes(extension);
  };

  if (!attachment) return null;

  const fileIcon = getFileIcon(attachment.filename);
  const isImageFile = isImage(attachment.filename);

  return (
    <Box 
      sx={{ 
        mt: 1,
        mb: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
        borderRadius: 2,
        overflow: 'hidden',
        maxWidth: '100%'
      }}
    >
      {isImageFile && attachment.url && (
        <Box sx={{ width: '100%', maxWidth: '400px', maxHeight: '250px', overflow: 'hidden' }}>
          <Box 
            component="img" 
            src={attachment.url} 
            alt={attachment.filename}
            sx={{ 
              width: '100%', 
              height: 'auto',
              objectFit: 'contain',
              cursor: 'pointer',
              '&:hover': { opacity: 0.9 }
            }}
            onClick={() => window.open(attachment.url, '_blank')}
          />
        </Box>
      )}
      
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          p: 1,
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Typography sx={{ fontSize: '1.5rem', mr: 1 }}>{fileIcon}</Typography>
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {attachment.filename}
          </Typography>
        </Box>
        <IconButton 
          size="small"
          onClick={() => window.open(attachment.url, '_blank')}
          sx={{ 
            color: darkMode ? 'primary.light' : 'primary.main', 
            bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)',
            ml: 1,
            '&:hover': {
              bgcolor: darkMode ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.5)',
            }
          }}
        >
          <Download size={18} />
        </IconButton>
      </Box>
    </Box>
  );
}; 
